import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiPhone, FiCalendar } = FiIcons;

const ContactBookingSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="contact-booking" className="py-20 bg-gradient-to-br from-accent-light/30 to-white" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-8 font-serif">
            Ready to <span className="gradient-text">Get Started?</span>
          </h2>
          
          {/* Contact Information */}
          <div className="grid md:grid-cols-3 gap-8 mb-12 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="flex flex-col items-center space-y-3 bg-white p-6 rounded-xl shadow-sm border border-gray-100"
            >
              <SafeIcon icon={FiMail} className="h-8 w-8 text-accent-magenta" />
              <p className="font-semibold text-gray-900">Email</p>
              <a 
                href="mailto:hello@wethebillions.com" 
                className="text-accent-magenta hover:text-accent-magenta/80 transition-colors font-medium"
              >
                hello@wethebillions.com
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="flex flex-col items-center space-y-3 bg-white p-6 rounded-xl shadow-sm border border-gray-100"
            >
              <SafeIcon icon={FiPhone} className="h-8 w-8 text-accent-magenta" />
              <p className="font-semibold text-gray-900">Call/Text</p>
              <a 
                href="tel:+12026560054" 
                className="text-accent-magenta hover:text-accent-magenta/80 transition-colors font-medium"
              >
                202-656-0054
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="flex flex-col items-center space-y-3 bg-white p-6 rounded-xl shadow-sm border border-gray-100"
            >
              <SafeIcon icon={FiCalendar} className="h-8 w-8 text-accent-magenta" />
              <p className="font-semibold text-gray-900">Book Online</p>
              <a 
                href="https://wethebillions.com/" 
                className="text-accent-magenta hover:text-accent-magenta/80 transition-colors font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                wethebillions.com
              </a>
            </motion.div>
          </div>

          {/* Booking Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">Book Your Consultation</h3>
            
            {/* Backup booking link */}
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Having trouble with the booking calendar? 
                <a 
                  href="https://wethebillions.neetocal.com/consultation" 
                  className="text-accent-magenta hover:text-accent-magenta/80 font-semibold ml-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here to book directly
                </a>
              </p>
            </div>

            {/* NeetoCal iframe embed */}
            <div className="w-full overflow-hidden rounded-xl border border-gray-200">
              <iframe
                allowFullScreen
                height="717"
                width="100%"
                frameBorder="0"
                title="Book Your Consultation - We The Billions"
                src="https://wethebillions.neetocal.com/embed/5f6e78fe-d1ce-43f6-b1ce-797e0cec501a"
                className="w-full"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactBookingSection;